import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
    Box, Typography, Button, TextField, Paper, Chip, Divider,
    Accordion, AccordionSummary, AccordionDetails,
    CircularProgress, IconButton, Tooltip, Snackbar, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions,
    FormControl, InputLabel, Select, MenuItem,
    useTheme, useMediaQuery,
} from '@mui/material';
import {
    ExpandMore, Download, Visibility as PreviewIcon,
    Comment, AttachFile, Lock, LockOpen, Close,
    Cancel as CancelIcon, Save, Refresh,
    PlayArrow, Pause,
} from '@mui/icons-material';
import { keyframes } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';

/** Readable disabled outlined buttons on DEFAULT + PROFESSIONAL light (labels + icons). */
const lightDisabledOutlinedButtonSx = (theme: Theme): SxProps<Theme> => {
    if (theme.palette.mode !== 'light') return {};
    const label = 'rgba(25, 25, 25, 0.38)';
    const border = 'rgba(25, 25, 25, 0.38)';
    const bg = 'rgba(25, 25, 25, 0.04)';
    return {
        '&.Mui-disabled': {
            opacity: 1,
            color: label,
            borderColor: border,
            backgroundColor: bg,
            WebkitTextFillColor: label,
            '& .MuiSvgIcon-root': { color: label },
        },
    };
};
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { DynamicBreadcrumbs } from 'components/Breadcrumb';
import { apiClient } from 'utils/ApiClient';
import { Logger } from 'utils/Logger';
import { API_BASE_URL } from 'envConfig';
import { LocalStorageAuth } from 'utils/LocalStorageAuth';

const liveBlinkAnimation = keyframes`
  0%, 100% { box-shadow: 0 0 4px rgba(76, 175, 80, 0.6); }
  50% { box-shadow: 0 0 14px rgba(76, 175, 80, 1), 0 0 28px rgba(76, 175, 80, 0.5); }
`;

const PRIORITY_LABELS: Record<string, string> = {
    P1: 'P1 - Blocker',
    P2: 'P2 - Urgent',
    P3: 'P3 - Important',
    P4: 'P4 - Minor',
};

const PRIORITY_COLORS: Record<string, 'error' | 'warning' | 'info' | 'default'> = {
    P1: 'error',
    P2: 'warning',
    P3: 'info',
    P4: 'default',
};

const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;

const RESOLUTION_OPTIONS = [
    'Resolution Confirmed',
    'Issue Obsolete or no longer occurs',
    'Resolution Irrelevant or no longer requires a resolution',
];

const isImageMimeType = (mimeType: string) => mimeType?.startsWith('image/');

const formatRelativeTime = (dateStr: string) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffDay === 0) {
        if (diffHour === 0) {
            if (diffMin < 1) return 'just now';
            return `${diffMin} min ago`;
        }
        return `${diffHour} hr ago`;
    }
    if (diffDay === 1) return 'yesterday';
    if (diffDay < 7) return `${diffDay} days ago`;
    if (diffDay < 30) return `${Math.floor(diffDay / 7)} weeks ago`;
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatTimestamp = (dateStr: string) =>
    new Date(dateStr).toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });

const formatTimestampParts = (dateStr: string) => {
    const d = new Date(dateStr);
    return {
        date: d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }),
        time: d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
    };
};

interface ThreadEntry {
    _id: string;
    sender: string;
    firstName: string;
    lastName: string;
    message: string;
    type: 'comment' | 'attachment';
    attachmentRef?: string;
    timestamp: string;
}

interface FileAttachmentMeta {
    _id: string;
    name: string;
    mimeType: string;
    size: number;
}

interface TicketData {
    _id: string;
    title: string;
    description: string;
    productName: string;
    priority: string;
    contactNumber: string;
    mobileNumber: string;
    emailAddress: string;
    ccEmailAddresses: string[];
    companyId: string;
    createdBy: string;
    ticketStatus: string;
    resolution: string;
    createdAt: string;
    updatedAt: string;
    fileAttachments: FileAttachmentMeta[];
    communicationThread: ThreadEntry[];
}

export const TicketDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isProfessionalLight = theme.palette.mode === 'light'
        && Boolean(theme.palette.dashboard?.heroTileForegroundColor);
    const timelineAccentColor = isProfessionalLight ? 'rgb(25, 25, 25)' : theme.palette.secondary.main;
    const user = useSelector((state: any) => state.authentication?.user);

    const [ticket, setTicket] = useState<TicketData | null>(null);
    const [loading, setLoading] = useState(true);
    const [accordionOpen, setAccordionOpen] = useState(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

    const [commentDialogOpen, setCommentDialogOpen] = useState(false);
    const [commentText, setCommentText] = useState('');
    const [commentSaving, setCommentSaving] = useState(false);

    const [attachSaving, setAttachSaving] = useState(false);
    const attachInputRef = useRef<HTMLInputElement>(null);

    const [closeDialogOpen, setCloseDialogOpen] = useState(false);
    const [closeResolution, setCloseResolution] = useState('');
    const [closeComment, setCloseComment] = useState('');
    const [closingTicket, setClosingTicket] = useState(false);
    const [reopeningTicket, setReopeningTicket] = useState(false);

    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    const [liveMode, setLiveMode] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const liveIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const prevThreadCountRef = useRef<number>(-1);

    const playChime = useCallback(() => {
        try {
            const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
            const playTone = (freq: number, startSec: number, durationSec: number) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'sine';
                osc.frequency.value = freq;
                gain.gain.setValueAtTime(0.18, ctx.currentTime + startSec);
                gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + startSec + durationSec);
                osc.connect(gain).connect(ctx.destination);
                osc.start(ctx.currentTime + startSec);
                osc.stop(ctx.currentTime + startSec + durationSec);
            };
            playTone(880, 0, 0.15);
            playTone(1175, 0.12, 0.18);
        } catch { /* audio not available */ }
    }, []);

    const firstName = user?.profile?.firstName || '';
    const lastName = user?.profile?.lastName || '';

    const fetchTicket = useCallback(async (silent = false) => {
        if (!id) return;
        if (!silent) setLoading(true);
        try {
            const response = await apiClient.get(`/support/ticket/${id}`);
            const data = response.data as TicketData;
            const newCount = data.communicationThread?.length ?? 0;

            if (silent && liveMode && prevThreadCountRef.current >= 0 && newCount > prevThreadCountRef.current) {
                playChime();
            }
            prevThreadCountRef.current = newCount;

            setTicket(data);
        } catch (err) {
            Logger.error('TicketDetail', `Failed to fetch ticket: ${err}`);
            if (!silent) setSnackbar({ open: true, message: 'Failed to load ticket.', severity: 'error' });
        } finally {
            if (!silent) setLoading(false);
        }
    }, [id, liveMode, playChime]);

    useEffect(() => { fetchTicket(); }, [fetchTicket]);

    const LIVE_POLL_INTERVAL_MS = 10_000;

    useEffect(() => {
        if (liveMode) {
            liveIntervalRef.current = setInterval(() => {
                fetchTicket(true);
            }, LIVE_POLL_INTERVAL_MS);
        }
        return () => {
            if (liveIntervalRef.current) {
                clearInterval(liveIntervalRef.current);
                liveIntervalRef.current = null;
            }
        };
    }, [liveMode, fetchTicket]);

    const handleManualRefresh = async () => {
        setRefreshing(true);
        await fetchTicket(true);
        setRefreshing(false);
    };

    const handleAddComment = async () => {
        if (!commentText.trim() || !id) return;
        setCommentSaving(true);
        try {
            await apiClient.post(`/support/ticket/${id}/message`, {
                message: commentText.trim(),
                firstName,
                lastName,
            });
            setCommentText('');
            setCommentDialogOpen(false);
            setSnackbar({ open: true, message: 'Comment added.', severity: 'success' });
            await fetchTicket();
        } catch (err) {
            Logger.error('TicketDetail', `Failed to add comment: ${err}`);
            setSnackbar({ open: true, message: 'Failed to add comment.', severity: 'error' });
        } finally {
            setCommentSaving(false);
        }
    };

    const handleAttachFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !id) return;
        e.target.value = '';

        if (file.size > MAX_FILE_SIZE_BYTES) {
            setSnackbar({ open: true, message: 'File exceeds 20 MB limit.', severity: 'error' });
            return;
        }

        setAttachSaving(true);
        try {
            const base64 = await fileToBase64(file);
            await apiClient.post(`/support/ticket/${id}/thread-attachment`, {
                fileAttachment: { data: base64, name: file.name, mimeType: file.type },
                firstName,
                lastName,
            });
            setSnackbar({ open: true, message: 'Attachment added.', severity: 'success' });
            await fetchTicket();
        } catch (err) {
            Logger.error('TicketDetail', `Failed to add attachment: ${err}`);
            setSnackbar({ open: true, message: 'Failed to add attachment.', severity: 'error' });
        } finally {
            setAttachSaving(false);
        }
    };

    const handleCloseTicket = async () => {
        if (!id || !closeResolution) return;
        setClosingTicket(true);
        try {
            await apiClient.put(`/support/ticket/${id}/close`, {
                resolution: closeResolution,
                comment: closeComment.trim() || undefined,
                firstName,
                lastName,
            });
            setCloseDialogOpen(false);
            setCloseResolution('');
            setCloseComment('');
            setSnackbar({ open: true, message: 'Ticket closed.', severity: 'success' });
            await fetchTicket();
        } catch (err) {
            Logger.error('TicketDetail', `Failed to close ticket: ${err}`);
            setSnackbar({ open: true, message: 'Failed to close ticket.', severity: 'error' });
        } finally {
            setClosingTicket(false);
        }
    };

    const handleReopenTicket = async () => {
        if (!id) return;
        setReopeningTicket(true);
        try {
            await apiClient.put(`/support/ticket/${id}/reopen`, { firstName, lastName });
            setSnackbar({ open: true, message: 'Ticket re-opened.', severity: 'success' });
            await fetchTicket();
        } catch (err) {
            Logger.error('TicketDetail', `Failed to re-open ticket: ${err}`);
            setSnackbar({ open: true, message: 'Failed to re-open ticket.', severity: 'error' });
        } finally {
            setReopeningTicket(false);
        }
    };

    const fetchAttachmentBlob = async (attachmentId: string): Promise<Blob | null> => {
        try {
            const url = `${API_BASE_URL}/support/ticket/${id}/attachment/${attachmentId}`;
            const headers = LocalStorageAuth.getAuthHeaders();
            const companyId = LocalStorageAuth.getActiveCompanyId();
            if (companyId) headers['X-Active-Company-Id'] = companyId;
            const response = await fetch(url, { headers });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return await response.blob();
        } catch (err) {
            Logger.error('TicketDetail', `Failed to fetch attachment: ${err}`);
            setSnackbar({ open: true, message: 'Failed to fetch attachment.', severity: 'error' });
            return null;
        }
    };

    const handleDownloadAttachment = async (attachmentId: string, fileName: string) => {
        const blob = await fetchAttachmentBlob(attachmentId);
        if (!blob) return;
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(blobUrl);
    };

    const handlePreviewAttachment = async (attachmentId: string) => {
        const blob = await fetchAttachmentBlob(attachmentId);
        if (!blob) return;
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(URL.createObjectURL(blob));
    };

    const fileToBase64 = (f: File): Promise<string> =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve((reader.result as string).split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(f);
        });

    const findAttachmentMeta = (refId?: string): FileAttachmentMeta | undefined => {
        if (!refId || !ticket) return undefined;
        return ticket.fileAttachments?.find(a => a._id === refId);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" p={6}>
                <CircularProgress color="secondary" />
            </Box>
        );
    }

    if (!ticket) {
        return (
            <Box pl={{ xs: 1.25, md: 2.5 }} pt={1} width="100%">
                <Typography color="error" variant="h6">Ticket not found.</Typography>
                <Button color="secondary" onClick={() => navigate('/support/my-tickets')} sx={{ mt: 2 }}>
                    Back to My Tickets
                </Button>
            </Box>
        );
    }

    const sortedThread = [...(ticket.communicationThread || [])].sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
    const isClosed = ticket.ticketStatus === 'CLOSED';

    return (
        <Box pl={{ xs: 1.25, md: 2.5 }} pt={1} width="100%" mb={3} pr={{ xs: 1.25, md: 2.5 }}>
            <DynamicBreadcrumbs
                items={[
                    { itemPath: '/dashboard', itemLabel: 'Home' },
                    { itemPath: '/support/my-tickets', itemLabel: 'My Tickets' },
                    { itemPath: `/support/my-tickets/${id}`, itemLabel: ticket._id },
                ]}
            />

            {/* Panel 1: Ticket Details Accordion */}
            <Accordion
                expanded={accordionOpen}
                onChange={() => setAccordionOpen(prev => !prev)}
                sx={{ mb: 3, borderRadius: 2, '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider' }}
                elevation={0}
            >
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box display="flex" alignItems="flex-start" width="100%" gap={1.5}>
                        <Typography variant="h6" component="span" fontWeight={700} sx={{ wordBreak: 'break-word', flex: 1, minWidth: 0 }}>
                            Case ID : {ticket._id} : {ticket.title}
                        </Typography>
                        {!isMobile && (
                            <Chip label={ticket.ticketStatus} size="small"
                                color={isClosed ? 'secondary' : 'success'}
                                sx={{ fontWeight: 600, flexShrink: 0, mt: 0.5 }} />
                        )}
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Box display="flex" gap={2} flexWrap="wrap">
                            <Box flex={1} minWidth={200}>
                                <Typography variant="caption" color="text.secondary">Priority</Typography>
                                <Box mt={0.25}>
                                    <Chip label={PRIORITY_LABELS[ticket.priority] || ticket.priority}
                                        size="small" color={PRIORITY_COLORS[ticket.priority] || 'default'} />
                                </Box>
                            </Box>
                            <Box flex={1} minWidth={200}>
                                <Typography variant="caption" color="text.secondary">Product</Typography>
                                <Typography variant="body2" fontWeight={500}>{ticket.productName}</Typography>
                            </Box>
                            <Box flex={1} minWidth={200}>
                                <Typography variant="caption" color="text.secondary">Status</Typography>
                                <Box mt={0.25}>
                                    <Chip label={ticket.ticketStatus} size="small"
                                        color={isClosed ? 'secondary' : 'success'} />
                                </Box>
                            </Box>
                        </Box>

                        {ticket.resolution && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">Resolution</Typography>
                                <Typography variant="body2" fontWeight={500}>{ticket.resolution}</Typography>
                            </Box>
                        )}

                        <Divider />

                        <Box>
                            <Typography variant="caption" color="text.secondary">Description</Typography>
                            <Paper variant="outlined" sx={{ p: 2, mt: 0.5, bgcolor: 'action.hover', whiteSpace: 'pre-wrap' }}>
                                <Typography variant="body2">{ticket.description}</Typography>
                            </Paper>
                        </Box>

                        <Divider />

                        <Box display="flex" gap={2} flexWrap="wrap">
                            <Box flex={1} minWidth={200}>
                                <Typography variant="caption" color="text.secondary">Contact Number</Typography>
                                <Typography variant="body2">{ticket.contactNumber || '-'}</Typography>
                            </Box>
                            <Box flex={1} minWidth={200}>
                                <Typography variant="caption" color="text.secondary">Mobile Number</Typography>
                                <Typography variant="body2">{ticket.mobileNumber || '-'}</Typography>
                            </Box>
                            <Box flex={1} minWidth={200}>
                                <Typography variant="caption" color="text.secondary">Email Address</Typography>
                                <Typography variant="body2">{ticket.emailAddress}</Typography>
                            </Box>
                        </Box>

                        {ticket.ccEmailAddresses?.length > 0 && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">CC Email Addresses</Typography>
                                <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                                    {ticket.ccEmailAddresses.map(email => (
                                        <Chip key={email} label={email} size="small" variant="outlined" />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        <Divider />

                        <Box display="flex" gap={2} flexWrap="wrap">
                            <Box flex={1} minWidth={200}>
                                <Typography variant="caption" color="text.secondary">Created By</Typography>
                                <Typography variant="body2">{ticket.createdBy}</Typography>
                            </Box>
                            <Box flex={1} minWidth={200}>
                                <Typography variant="caption" color="text.secondary">Created At</Typography>
                                <Typography variant="body2">{formatTimestamp(ticket.createdAt)}</Typography>
                            </Box>
                            <Box flex={1} minWidth={200}>
                                <Typography variant="caption" color="text.secondary">Last Updated</Typography>
                                <Typography variant="body2">{formatTimestamp(ticket.updatedAt)}</Typography>
                            </Box>
                        </Box>

                        {/* Attachments */}
                        {ticket.fileAttachments?.length > 0 && (
                            <>
                                <Divider />
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                        Attachments ({ticket.fileAttachments.length})
                                    </Typography>
                                    <Box display="flex" flexDirection="column" gap={1}>
                                        {ticket.fileAttachments.map(att => (
                                            <Box key={att._id} display="flex" alignItems="center" gap={1}
                                                sx={{ px: 1.5, py: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                                                <AttachFile fontSize="small" color="secondary" />
                                                <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {att.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {(att.size / (1024 * 1024)).toFixed(1)} MB
                                                </Typography>
                                                {isImageMimeType(att.mimeType) && (
                                                    <Tooltip title="Preview">
                                                        <IconButton size="small" color="secondary" onClick={() => handlePreviewAttachment(att._id)}>
                                                            <PreviewIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title="Download">
                                                    <IconButton size="small" color="secondary" onClick={() => handleDownloadAttachment(att._id, att.name)}>
                                                        <Download fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </>
                        )}
                    </Box>
                </AccordionDetails>
            </Accordion>

            {/* Panel 2: Discussion Thread */}
            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={2}>
                    <Typography variant="h6" component="span" fontWeight={700}>
                        Discussion
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Tooltip title="Refresh discussion">
                            <span>
                                <IconButton
                                    color={isProfessionalLight ? undefined : 'secondary'}
                                    onClick={handleManualRefresh}
                                    disabled={refreshing}
                                    size="small"
                                    sx={{
                                        border: '1px solid',
                                        borderColor: isProfessionalLight ? 'rgba(25, 25, 25, 0.38)' : 'secondary.main',
                                        color: timelineAccentColor,
                                        borderRadius: 1,
                                        width: 36,
                                        height: 36,
                                        ...(isProfessionalLight && {
                                            '&.Mui-disabled': {
                                                color: 'rgba(25, 25, 25, 0.38)',
                                                borderColor: 'rgba(25, 25, 25, 0.26)',
                                            },
                                        }),
                                    }}
                                >
                                    {refreshing ? (
                                        <CircularProgress size={20} sx={{ color: timelineAccentColor }} />
                                    ) : (
                                        <Refresh />
                                    )}
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Button
                            variant={liveMode ? 'contained' : 'outlined'}
                            color={liveMode ? 'success' : 'secondary'}
                            size="small"
                            startIcon={liveMode ? <Pause sx={{ fontSize: 16 }} /> : <PlayArrow sx={{ fontSize: 16 }} />}
                            onClick={() => setLiveMode(prev => !prev)}
                            sx={{
                                textTransform: 'none', fontWeight: 600, px: 1.5, whiteSpace: 'nowrap',
                                ...(liveMode && {
                                    animation: `${liveBlinkAnimation} 1.5s ease-in-out infinite`,
                                    backgroundColor: 'success.main',
                                    color: '#fff',
                                    '&:hover': { backgroundColor: 'success.dark' },
                                }),
                            }}
                        >
                            Live
                        </Button>
                    </Box>
                </Box>

                {/* Action bar */}
                <Box display="flex" gap={1.5} flexWrap="wrap" mb={3} sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
                    <Button variant="outlined" color={isClosed ? 'error' : 'secondary'} startIcon={<Comment />}
                        onClick={() => setCommentDialogOpen(true)}
                        disabled={isClosed} size="small" fullWidth={isMobile}
                        sx={lightDisabledOutlinedButtonSx(theme)}>
                        Add Comment
                    </Button>
                    <Button variant="outlined" color={isClosed ? 'error' : 'secondary'} startIcon={<AttachFile />}
                        onClick={() => attachInputRef.current?.click()}
                        disabled={isClosed || attachSaving} size="small" fullWidth={isMobile}
                        sx={lightDisabledOutlinedButtonSx(theme)}>
                        {attachSaving ? 'Uploading...' : 'Add Attachment'}
                    </Button>
                    <input ref={attachInputRef} type="file" hidden onChange={handleAttachFile}
                        accept="image/*,video/*,.csv,.xls,.xlsx" />
                    <Button variant="outlined" color="error" startIcon={<Lock />}
                        onClick={() => setCloseDialogOpen(true)}
                        disabled={isClosed}
                        size="small" fullWidth={isMobile}
                        sx={lightDisabledOutlinedButtonSx(theme)}>
                        {isClosed ? 'Ticket Closed' : 'Close Ticket'}
                    </Button>
                    {isClosed && (
                        <Button variant="outlined" color="secondary" startIcon={<LockOpen />}
                            onClick={handleReopenTicket}
                            disabled={reopeningTicket}
                            size="small" fullWidth={isMobile}
                            sx={lightDisabledOutlinedButtonSx(theme)}>
                            {reopeningTicket ? 'Re-opening...' : 'Re-Open'}
                        </Button>
                    )}
                </Box>

                {/* Timeline */}
                {sortedThread.length === 0 ? (
                    <Typography color="text.secondary" variant="body2" sx={{ py: 2, textAlign: 'center' }}>
                        No discussion yet. Add a comment to start the conversation.
                    </Typography>
                ) : (
                    <Box sx={{ position: 'relative', pl: '40px', pb: 4, minWidth: 0 }}>
                        <Box sx={{
                            position: 'absolute', left: '15px', top: 0, bottom: 0, width: '2px',
                            bgcolor: timelineAccentColor,
                            opacity: isProfessionalLight ? 0.45 : 0.35,
                        }} />

                        {sortedThread.map((entry) => {
                            const attMeta = entry.type === 'attachment' ? findAttachmentMeta(entry.attachmentRef) : undefined;
                            const authorName = [entry.firstName, entry.lastName].filter(Boolean).join(' ') || entry.sender;
                            const { date, time } = formatTimestampParts(entry.timestamp);
                            const secondaryMain = theme.palette.secondary.main;

                            return (
                                <Box key={entry._id} sx={{ position: 'relative', mb: 2.5, display: 'flex', alignItems: 'flex-start', minWidth: 0 }}>
                                    <Box sx={{
                                        position: 'absolute', left: '-33px', top: '16px', width: 14, height: 14, borderRadius: '50%',
                                        bgcolor: timelineAccentColor,
                                        border: '2px solid', borderColor: 'background.paper',
                                        boxShadow: isProfessionalLight
                                            ? '0 0 0 3px rgba(25, 25, 25, 0.15)'
                                            : `0 0 0 3px ${secondaryMain}30`,
                                        zIndex: 1,
                                    }} />
                                    <Box sx={{
                                        position: 'absolute', left: '-19px', top: '22px', width: '19px', height: '2px',
                                        bgcolor: timelineAccentColor,
                                        opacity: isProfessionalLight ? 0.65 : 0.6,
                                    }} />

                                    <Paper elevation={0} sx={{
                                        flex: 1, minWidth: 0, p: { xs: 1.5, md: 2 }, border: '1px solid', borderColor: `${secondaryMain}40`, borderRadius: 2,
                                        borderLeft: '3px solid', borderLeftColor: 'secondary.main',
                                        transition: 'box-shadow 0.2s, border-color 0.2s',
                                        '&:hover': { boxShadow: theme.shadows[3], borderColor: 'secondary.main' },
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap', mb: 0.75 }}>
                                            <Chip label={authorName} size="small" color="secondary" sx={{
                                                fontWeight: 600, fontSize: '0.75rem', height: 'auto',
                                                '& .MuiChip-label': { px: 1, whiteSpace: 'normal', lineHeight: 1.4 }, py: 0.25,
                                            }} />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    ml: { xs: 0, sm: 'auto' },
                                                    whiteSpace: 'nowrap',
                                                    fontWeight: 600,
                                                    fontSize: '0.875rem',
                                                    color: timelineAccentColor,
                                                }}
                                            >
                                                {date} &middot; {time}
                                            </Typography>
                                        </Box>

                                        {entry.type === 'comment' ? (
                                            <Typography variant="body2" sx={{ color: 'text.primary', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                                                {entry.message}
                                            </Typography>
                                        ) : (
                                            <Box>
                                                <Typography variant="body2" sx={{ color: 'text.primary', mb: 0.5, wordBreak: 'break-word' }}>
                                                    {entry.message}
                                                </Typography>
                                                {attMeta && (
                                                    <Box display="flex" alignItems="center" gap={1}
                                                        sx={{ p: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider', mt: 0.5 }}>
                                                        <AttachFile fontSize="small" color="secondary" />
                                                        <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {attMeta.name}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {(attMeta.size / (1024 * 1024)).toFixed(1)} MB
                                                        </Typography>
                                                        {isImageMimeType(attMeta.mimeType) && (
                                                            <Tooltip title="Preview">
                                                                <IconButton size="small" color="secondary" onClick={() => handlePreviewAttachment(attMeta._id)}>
                                                                    <PreviewIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                        <Tooltip title="Download">
                                                            <IconButton size="small" color="secondary" onClick={() => handleDownloadAttachment(attMeta._id, attMeta.name)}>
                                                                <Download fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                )}
                                            </Box>
                                        )}

                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', wordBreak: 'break-all' }}>
                                            {formatRelativeTime(entry.timestamp)}
                                        </Typography>
                                    </Paper>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Paper>

            {/* Add Comment Dialog */}
            <Dialog open={commentDialogOpen} onClose={() => !commentSaving && setCommentDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" component="span" fontWeight={700}>Add Comment</Typography>
                    <IconButton onClick={() => setCommentDialogOpen(false)} disabled={commentSaving}><Close /></IconButton>
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        multiline
                        minRows={4}
                        maxRows={10}
                        fullWidth
                        color="secondary"
                        label="Your comment"
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        disabled={commentSaving}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" color="secondary" onClick={() => setCommentDialogOpen(false)}
                        disabled={commentSaving} startIcon={<CancelIcon />}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="success" onClick={handleAddComment}
                        disabled={commentSaving || !commentText.trim()} startIcon={commentSaving ? <CircularProgress size={18} color="inherit" /> : <Save />}>
                        {commentSaving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Close Ticket Dialog */}
            <Dialog open={closeDialogOpen} onClose={() => !closingTicket && setCloseDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" component="span" fontWeight={700}>Close Ticket</Typography>
                    <IconButton onClick={() => setCloseDialogOpen(false)} disabled={closingTicket}><Close /></IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2.5} mt={1}>
                        <FormControl fullWidth required color="secondary">
                            <InputLabel>Resolution Status</InputLabel>
                            <Select
                                value={closeResolution}
                                label="Resolution Status"
                                onChange={(e) => setCloseResolution(e.target.value)}
                                disabled={closingTicket}
                            >
                                {RESOLUTION_OPTIONS.map((opt) => (
                                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            multiline
                            minRows={3}
                            maxRows={8}
                            fullWidth
                            color="secondary"
                            label="Comment (optional)"
                            value={closeComment}
                            onChange={(e) => setCloseComment(e.target.value)}
                            disabled={closingTicket}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" color="secondary"
                        onClick={() => { setCloseDialogOpen(false); setCloseResolution(''); setCloseComment(''); }}
                        disabled={closingTicket} startIcon={<CancelIcon />}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="success" onClick={handleCloseTicket}
                        disabled={closingTicket || !closeResolution}
                        startIcon={closingTicket ? <CircularProgress size={18} color="inherit" /> : <Save />}>
                        {closingTicket ? 'Closing...' : 'Submit'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Image Preview Dialog */}
            <Dialog open={!!previewUrl} onClose={() => setPreviewUrl(null)} maxWidth="lg">
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" component="span" fontWeight={700}>Preview</Typography>
                    <IconButton onClick={() => setPreviewUrl(null)}><Close /></IconButton>
                </DialogTitle>
                <DialogContent>
                    {previewUrl && (
                        <Box display="flex" justifyContent="center">
                            <img src={previewUrl} alt="Preview" style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default TicketDetail;
